import { Component, Inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import {
  MemberQuery,
  DATE_OPTIONS,
  DateOptions,
  RewardsWalletQuery,
  MarketplaceWalletItem,
  RewardsWalletItem,
  MarketplaceItemAttributeDisplayType,
  PartnerQuery,
  Partner,
  PurchaseQuery,
  RewardsWalletService,
  toNumber,
} from '@fgb/core';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { Location } from '@angular/common';
import { OwlOptions } from 'ngx-owl-carousel-o';
import { OwlCarouselSettings } from '@fgb/portal-component-library/src/lib/shared/utilities';
import { ScreenType } from '@fgb/portal-component-library/src/lib/directives';
import { DateTime } from 'luxon';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';

export interface MarketplaceWalletDetailsOptions {
  showTransferButton?: boolean;
}

@Component({
  selector: 'fgbcl-marketplace-wallet-details',
  templateUrl: './marketplace-wallet-details.component.html',
  styleUrls: ['./marketplace-wallet-details.component.scss'],
})
export class MarketplaceWalletDetailsComponent implements OnInit, OnDestroy {
  purchasedItem: MarketplaceWalletItem;
  purchasedItemList: any[];
  @Input() id: string;
  @Input() options: MarketplaceWalletDetailsOptions;
  private _destroyed$ = new Subject<void>();
  screenType = ScreenType;
  walletItems$: Observable<RewardsWalletItem[]>;
  groupedItem: any;
  groupPoints: { points: number; ecash: number; merchantValue: number };
  desktopOptions: OwlOptions = {
    dots: true,
    margin: 8,
    pullDrag: false,
    autoWidth: true,
    autoplayTimeout: OwlCarouselSettings.AutoplayTimeout,
    autoplaySpeed: OwlCarouselSettings.AutoplaySpeed,
    navSpeed: 700,
    responsive: {
      0: { items: 1, autoWidth: true },
      540: { items: 1, autoWidth: true },
      740: { items: 1 },
    },
  };
  private defaultOptions: MarketplaceWalletDetailsOptions = { showTransferButton: false };
  inputNumber: number = 1;
  inputMax: number = 10;

  mobileOptions: OwlOptions = {
    dots: true,
    margin: 8,
    pullDrag: false,
    autoWidth: true,
    autoplayTimeout: OwlCarouselSettings.AutoplayTimeout,
    autoplaySpeed: OwlCarouselSettings.AutoplaySpeed,
    navSpeed: 700,
    responsive: {
      0: { items: 1, autoWidth: true },
      540: { items: 2, autoWidth: false },
      740: { items: 3 },
    },
  };
  attributeDisplayType = MarketplaceItemAttributeDisplayType;
  expired = signal(false);
  partners$: Observable<Partner[]>;
  selectedPartner: Partner | null = null;
  isDateAttribute: boolean = false;
  errorCode: string = '';
  hasError: boolean = false;

  constructor(
    @Inject(DATE_OPTIONS) public dateOption: DateOptions,
    private memberQuery: MemberQuery,
    private location: Location,
    private rewardsWalletQuery: RewardsWalletQuery,
    private modalService: NgbModal,
    private partnerQuery: PartnerQuery,
    private purchaseQuery: PurchaseQuery,
    private rewardsWalletService: RewardsWalletService,
    private router: Router
  ) {}

  ngOnInit() {
    this.partners$ = this.partnerQuery.selectAll();
    this.options = { ...this.defaultOptions, ...this.options };
    combineLatest([this.rewardsWalletQuery.selectRewardWalletItem(this.id), this.memberQuery.userContext$])
      .pipe(
        switchMap(([purchase, userContext]) =>
          this.rewardsWalletQuery.selectItemsByGroupId(purchase?.GroupId).pipe(map((group) => [purchase, userContext, group]))
        ),
        takeUntil(this._destroyed$)
      )
      .subscribe(([purchase, userContext, group]) => {
        if (!purchase || !userContext) {
          return;
        }
        this.purchasedItem = purchase as MarketplaceWalletItem;
        const isExpired = DateTime.fromISO(this.purchasedItem.ExpireDate as string, { zone: 'utc' }) < DateTime.utc();
        this.expired.set(isExpired);
        this.groupedItem = group;

        this.inputMax = this.groupedItem.items.length;
        this.isDateAttribute = this.purchasedItem.Attributes?.some(
          (attr) =>
            attr.DisplayType === MarketplaceItemAttributeDisplayType.Date ||
            attr.DisplayType === MarketplaceItemAttributeDisplayType.Date_No_Expire
        );

        const totalPoints = this.groupedItem?.items.reduce((acc: number, item: any) => acc + item.PointValue, 0);
        const totalEcash = this.groupedItem?.items.reduce((acc: number, item: any) => acc + item.EcashValue, 0);
        const totalMerchantValue = this.groupedItem?.items.reduce((acc: number, item: any) => acc + item.MerchantValue, 0);
        this.groupPoints = { points: totalPoints, ecash: totalEcash, merchantValue: totalMerchantValue };
      });
  }

  get isUniqueCodeLink() {
    return this.purchasedItem.Code.toString().includes('http://') || this.purchasedItem.Code.toString().includes('https://');
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  backToWallet() {
    this.location.back();
  }

  openTransferModal(content: any): void {
    this.modalService.open(content, { windowClass: 'wallet-transfer-item-modal', centered: true, size: 'lg' });
  }

  increase(): void {
    if (this.inputNumber < this.inputMax) {
      this.inputNumber++;
    }
  }

  decrease(): void {
    if (this.inputNumber > 1) {
      this.inputNumber--;
    }
  }

  transferItem(): void {
    if (!this.selectedPartner) return;
    this.clearErrorMessage();

    const items = this.groupedItem.items
      .slice(0, this.inputNumber)
      .map((item: any) => this.purchaseQuery.getPurchaseById(item.LogId))
      .map((purchase: any) => ({
        TransferLogId: purchase.LogId,
        TransferOrderId: purchase.OrderId,
        TransferPortalId: this.selectedPartner!.PortalId,
      }));
    this.rewardsWalletService.transferItem(items).subscribe({
      next: () => {
        sessionStorage.setItem(
          'transferSuccess',
          JSON.stringify({
            qty: this.inputNumber,
            purchasedItem: this.purchasedItem,
            transferredTo: this.selectedPartner,
          })
        );
        this.modalService.dismissAll();
        this.router.navigate(['/rewards/wallet/transfer/success']);
      },
      error: (err) => {
        if (err?.error.Code) {
          this.errorCode = err.error.Code;
        } else {
          this.errorCode = 'unknown';
        }
        this.hasError = true;
      },
    });
  }

  onTransferItemInput(event: any): void {
    const number = toNumber(event.target.value);
    if (number > this.inputMax) {
      this.inputNumber = this.inputMax;
    } else if (number < 1) {
      this.inputNumber = 1;
    }

    event.target.value = this.inputNumber;
  }

  onPartnerSelect(event: any): void {
    this.clearErrorMessage();
  }

  private clearErrorMessage(): void {
    this.hasError = false;
    this.errorCode = '';
  }
}
